import React from 'react';
import { Award, Shield, Eye, Clock } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../utils/mockData';

const WhyChoose = () => {
  const { language } = useLanguage();
  const t = translations[language];

  const features = [
    {
      icon: Award,
      title: t.whyChoose.quality.title,
      description: t.whyChoose.quality.description,
      color: 'from-blue-500 to-cyan-500',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600'
    },
    {
      icon: Shield,
      title: t.whyChoose.trust.title,
      description: t.whyChoose.trust.description,
      color: 'from-emerald-500 to-teal-500',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600'
    },
    {
      icon: Eye,
      title: t.whyChoose.detail.title,
      description: t.whyChoose.detail.description,
      color: 'from-amber-500 to-orange-500',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600'
    },
    {
      icon: Clock,
      title: t.whyChoose.punctuality.title,
      description: t.whyChoose.punctuality.description,
      color: 'from-slate-500 to-slate-600',
      iconBg: 'bg-slate-100',
      iconColor: 'text-slate-600'
    }
  ];

  return (
    <section id="why-choose" className="py-20 bg-gradient-to-b from-slate-50 to-white">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Arial, sans-serif' }}>
              {t.whyChoose.title}
            </h2>
            <p className="text-xl text-slate-600 mb-6">
              {t.whyChoose.subtitle}
            </p>
            <div className="w-24 h-1 bg-amber-600 mx-auto"></div>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div 
                  key={index}
                  className="group bg-white p-8 rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-slate-100"
                >
                  <div className={`${feature.iconBg} w-20 h-20 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <IconComponent className={`h-10 w-10 ${feature.iconColor}`} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Arial, sans-serif' }}>
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Stats Section */}
          <div className="mt-20 bg-gradient-to-r from-black to-zinc-900 rounded-2xl p-12 shadow-2xl border border-yellow-600">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div className="border-r border-gray-700 last:border-r-0">
                <div className="text-4xl md:text-5xl font-bold text-yellow-600 mb-2">8+</div>
                <div className="text-gray-300">
                  {language === 'en' ? 'Years Experience' : 'Años de Experiencia'}
                </div>
              </div>
              <div className="border-r border-gray-700 last:border-r-0">
                <div className="text-4xl md:text-5xl font-bold text-yellow-600 mb-2">100+</div>
                <div className="text-gray-300">
                  {language === 'en' ? 'Projects' : 'Proyectos'}
                </div>
              </div>
              <div className="border-r border-gray-700 last:border-r-0">
                <div className="text-4xl md:text-5xl font-bold text-yellow-600 mb-2">100%</div>
                <div className="text-gray-300">
                  {language === 'en' ? 'Satisfaction' : 'Satisfacción'}
                </div>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-bold text-yellow-600 mb-2">24/7</div>
                <div className="text-gray-300">
                  {language === 'en' ? 'Support' : 'Soporte'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChoose;
